'use client'

import { AdditionalInformation } from '@components/app/ListingPage/AdditionalInformation'
import { ClosestLocation } from '@components/app/ListingPage/ClosestLocation'
import {
  EffectorEfficacy,
  HengenixEfficacy,
} from '@components/app/ListingPage/EfficacyCharts'
import { ColorfulEligibilityCriteria } from '@components/app/ListingPage/EligibilityCriteria/ColorfulEligibilityCriteria'
import { HardcodedHengenixPaidContent } from '@components/app/ListingPage/HengenixPaidAccomodation'
import { LogisticInfo } from '@components/app/ListingPage/LogisticInfo'
import { SiteInfoPanel } from '@components/app/ListingPage/SiteInfoPanel'
import StructuredDataForTrial from '@components/app/ListingPage/StructuredDataForTrial'
import { ListingSection } from '@components/app/ListingPage/TableOfContents/ListingSection'
import TreatmentsAndConditions from '@components/app/ListingPage/TreatmentsAndConditions'
import ConditionsCard from '@components/app/ListingPage/TreatmentsAndConditions/ConditionsCard'
import { TrialDesign } from '@components/app/ListingPage/TrialDesign'
import { TrialListingHeader } from '@components/app/ListingPage/TrialListingHeader'
import { NavBar } from '@components/app/NavBar'
import RelatedArticleLinks from '@components/app/RelatedLinkSection/RelatedArticleLinks'
import RelatedListingLinks from '@components/app/RelatedLinkSection/RelatedListingLinks'
import RelatedTreatmentTopicLinks from '@components/app/RelatedLinkSection/RelatedTreatmentTopicLinks'
import { AwardBadgeCards } from '@components/app/TrialAwardBadges/AwardBadgeCards'
import { GeolocationContext } from '@components/hooks/GeolocationProvider/index.client'
import { ReferralBanner } from '@components/primitives/Banner/ReferralBanner.client'
import { TimelineCard } from '@components/primitives/Chart/Timeline/TimelineCard'
import NoIndex from '@components/primitives/Layout/HeaderComponents/NoIndex'
import Link from '@components/primitives/Link'
import { Text } from '@components/primitives/Text'
import VideoPlayer from '@components/primitives/VideoPlayer/index.client'
import getTrialLocationsNearLatLng, {
  type TrialLocationInfo,
} from '@lib/api/patients/trialLocations/getTrialLocationsNearLatLng'
import Routes from '@lib/routes'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import {
  EFFECTOR_NSCLC,
  HENGENIX_SCLC_EXTENSIVE_STAGE,
  HENGENIX_SCLC_LIMITED_STAGE,
  IGM_BIO_TRIAL,
} from '@modules/trialRecommendations/handleRecommendationsByColloquialTermStripped'
import { MINDMED_GAD_PHASE_3 } from '@modules/trials/constants/trials'
import getPreferredTrialTitle from '@modules/trials/helpers/getPreferredTrialTitle'
import {
  isArrivoBioMdd,
  isMindMedGad3,
  isNeurocrineMdd,
  isOtsukaMdd,
} from '@modules/trials/helpers/isHardcodedTrial'
import getParsedEligibilityCriteriaForTrial from '@modules/trials/helpers/parseEligibilityCriteria/getParsedEligibilityCriteriaForTrial'
import { TrialOverridesTypeValidator } from '@modules/trials/types/TrialOverrides'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import Trial2ListingMap from '../LocationMap/Trial2ListingMap.client'
import { RelatedConditionCarousel } from '../RelatedLinkSection/RelatedConditionCarousel'
import TrialRagFaqWidget from '../TrialRagFaqWidget'
import { EligibilityCountCard } from './EligibilityCountCard'
import { FloatingApplyCta } from './FloatingApplyCta'
import { HeroWithoutGradient } from './ListingHero/HeroWithoutGradient.client'
import { NavbarTableOfContents } from './NavbarTableOfContents'
import { WhoIsRunningTheClinicalTrial } from './ResponsibleParty/WhoIsRunningTheClinicalTrial'
import { StudyObjectivesSection } from './StudyObjectives'
import getArmGroupsForTrialDesign from './TrialDesign/getArmGroupsForTrialDesign'
import { TrialFaqAccordions } from './TrialFaqAccordions'
import { TrialMediaLibrary } from './TrialMediaLibrary'
import { TrialNavBarMenu } from './TrialNavBarMenu'
import { type LegacyTrialPageProps } from './getLegacyTrialListingPageProps'

// This is the pages router page
export function TrialListingPage({
  conjuredFaqCitations,
  isMonetizedTrial,
  notIndexed,
  relatedArticles,
  relatedDirectories,
  relatedListings,
  relatedTreatmentTopics,
  shareTrialCodes,
  showConjuredFaq,
  showMediaLibrary,
  showOtherTrialsToConsider,
  showPopularCategories,
  showRelatedArticleLinks,
  showResearchHighlights,
  trial,
}: LegacyTrialPageProps) {
  const userLocation = useContext(GeolocationContext)
  const [trialLocationInfo, setTrialLocationInfo] =
    useState<TrialLocationInfo>()
  const ctaColor = 'primary'

  const validatedOverridesResult = TrialOverridesTypeValidator.safeParse(
    trial.overrides,
  )

  const overrides = validatedOverridesResult.success
    ? validatedOverridesResult.data
    : null

  useEffect(() => {
    if (
      userLocation.latitude &&
      userLocation.longitude &&
      !trialLocationInfo &&
      trial.slug
    ) {
      getTrialLocationsNearLatLng({
        latlng: {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude,
        },
        slug: trial.slug,
      }).then((locationInfo) => setTrialLocationInfo(locationInfo))
    }
  }, [userLocation])

  const trialTitle = getPreferredTrialTitle(trial)

  const showConjuredRagFaq = useMemo(() => {
    if (!trial.conjuredFaq) {
      return false
    }

    const keysToCheck = [
      'mechanismOfAction',
      'sideEffects',
      'priorApprovals',
      'promisingAlternatives',
    ]
    let count = 0

    for (const key of keysToCheck) {
      if (
        key in trial.conjuredFaq &&
        trial.conjuredFaq[key as keyof typeof trial.conjuredFaq] !== null
      ) {
        count++
      }
    }

    return count >= 2
  }, [trial.conjuredFaq])

  const awardBadges = (trial.awardBadges ?? []).filter(
    (badge) => badge.deletedAt === null,
  )
  const hasAwardBadges = awardBadges.length > 0

  const showTreatmentDetailsSection =
    !isNeurocrineMdd(trial.nctId) &&
    (trial.outcomes.length > 0 || hasAwardBadges)

  /* eslint-disable sort-keys */
  const sectionRefs = {
    summary: useInView({ threshold: 0.7 }),
    eligibilityCriteria: useInView({ threshold: 0.7 }),
    timeline: useInView({ threshold: 0.7 }),
    treatmentDetails: useInView({ threshold: 0.7 }),
    trialDesign: useInView({ threshold: 0.7 }),
    findASite: useInView({ threshold: 0.3 }),
    mediaLibrary: useInView({ threshold: 0.7 }),
    faq: useInView({ threshold: 0.7 }),
  }
  /* eslint-enable sort-keys */

  const [heroRef, isHeroInView] = useInView({ threshold: 0 })

  return (
    <>
      {notIndexed && <NoIndex />}
      <TrialListingHeader trial={trial} trialTitle={trialTitle} />
      <StructuredDataForTrial trial={trial} />
      <div className='grid grid-cols-1'>
        <div className='hidden md:inline-block'>
          <NavBar
            containerClassName='border-b border-neutral200 bg-white drop-shadow-sm'
            includeNavigationLinks={false}
            maxWidth='max-w-screen-xl'
            navbarMenu={
              <TrialNavBarMenu
                ctaColor={ctaColor}
                isHeroInView={isHeroInView}
                shareTrialCodes={shareTrialCodes}
                trial={trial}
              />
            }
            showLogo={false}
          >
            <NavbarTableOfContents sectionRefs={sectionRefs} />
          </NavBar>
        </div>
        <div className='inline-block sm:hidden'>
          <NavBar includeNavigationLinks={false} maxWidth='max-w-screen-xl' />
        </div>

        <ReferralBanner />

        <div className='mt-4 flex flex-col'>
          {/* Add a link for user to go back to search while perserving the filters  */}
          <Link href={urlWithQueryParams(Routes.search)}>
            <Text
              className={`mb-2 cursor-pointer text-neutral600 hover:underline`}
              element='p'
              styleName='p-small'
              value={'← Back to Search'}
            />
          </Link>
          <HeroWithoutGradient
            heroRef={heroRef}
            isMonetizedTrial={isMonetizedTrial}
            nearestTrialLocations={trialLocationInfo?.nearestTrialLocations}
            shareTrialCodes={shareTrialCodes}
            trial={trial}
          />
        </div>

        <div className='flex w-full justify-between'>
          <div className='mt-12 flex w-full flex-col space-y-12 lg:w-3/4 lg:space-y-16'>
            <ListingSection
              id='summary'
              innerRef={sectionRefs.summary.ref}
              title='Summary'
            >
              <div className='flex flex-col gap-4'>
                <TreatmentsAndConditions trial={trial} />

                {trial.conjuredFaq && showConjuredFaq ? (
                  <TrialFaqAccordions
                    conjuredFaq={trial.conjuredFaq}
                    hideCheckEligibilityLink={
                      !sectionRefs.eligibilityCriteria.entry
                    }
                    hideStudyDesignLink={!sectionRefs.trialDesign.entry}
                  />
                ) : (
                  <ConditionsCard conditions={trial.conditions} />
                )}
              </div>
            </ListingSection>
            <ListingSection
              hide={getParsedEligibilityCriteriaForTrial(trial).length === 0}
              id='eligibilityCriteria'
              innerRef={sectionRefs.eligibilityCriteria.ref}
              title='Eligibility Criteria'
            >
              <ColorfulEligibilityCriteria trial={trial} />
              {trial.video && (
                <VideoPlayer
                  containerClassName='bg-neutral50 rounded-xl p-4'
                  trackingSource={TrackingSources.LISTING}
                  videoId={trial.video.externalVideoId}
                />
              )}
            </ListingSection>
            <HardcodedHengenixPaidContent nctId={trial.nctId} />

            {/* GTM Uses these as examples of Overrides Don't Clean Up */}
            {[
              HENGENIX_SCLC_EXTENSIVE_STAGE.nctId,
              HENGENIX_SCLC_LIMITED_STAGE.nctId,
            ].includes(trial.nctId) && <HengenixEfficacy />}

            {[EFFECTOR_NSCLC.nctId].includes(trial.nctId) && (
              <EffectorEfficacy />
            )}

            <ListingSection
              id='timeline'
              innerRef={sectionRefs.timeline.ref}
              title='Timeline'
            >
              <TimelineCard trial={trial} />
            </ListingSection>

            {showTreatmentDetailsSection && (
              <ListingSection
                id='treatmentDetails'
                innerRef={sectionRefs.treatmentDetails.ref}
                title={
                  // Sponsor request
                  isOtsukaMdd(trial.nctId) || isArrivoBioMdd(trial.nctId)
                    ? 'Study Treatment Details'
                    : 'Treatment Details'
                }
              >
                <div className='flex flex-col gap-8'>
                  <StudyObjectivesSection trial={trial} />

                  {hasAwardBadges && !isMindMedGad3(trial.nctId) && (
                    <div className='flex w-full flex-col space-y-2'>
                      <Text
                        className='text-neutral900'
                        element={'h4'}
                        styleName='h5'
                        value='Awards & Highlights'
                      />

                      <AwardBadgeCards awardBadges={awardBadges} />
                    </div>
                  )}
                </div>
              </ListingSection>
            )}

            {getArmGroupsForTrialDesign(trial).length > 0 ? (
              <ListingSection
                id='trialDesign'
                innerRef={sectionRefs.trialDesign.ref}
                title={
                  trial.nctId === MINDMED_GAD_PHASE_3.nctId
                    ? 'Study Design'
                    : 'Trial Design'
                }
              >
                <TrialDesign trial={trial} />
              </ListingSection>
            ) : (
              <></>
            )}
          </div>
          {!isNeurocrineMdd(trial.nctId) && (
            <div className='ml-8 hidden lg:block lg:w-80 lg:flex-none'>
              <div className='sticky top-24 mt-12 self-start rounded-2xl border border-neutral200 p-6'>
                <EligibilityCountCard buttonColor={ctaColor} trial={trial} />
              </div>
            </div>
          )}
        </div>

        <div className='my-12 flex flex-col gap-12 lg:mt-16 lg:gap-16'>
          {showResearchHighlights &&
            showConjuredRagFaq &&
            trial.conjuredFaq && (
              <ListingSection id={'faq'} title='Research Highlights'>
                <Text
                  className='pb-2 italic text-neutral500'
                  styleName='p-small'
                  value='Information in this section is not a recommendation. We encourage patients to speak with their healthcare team when evaluating any treatment decision.'
                ></Text>
                <TrialRagFaqWidget
                  conjuredFaqCitations={conjuredFaqCitations}
                  faqsWithRag={trial.conjuredFaq}
                />
              </ListingSection>
            )}
          <ListingSection
            hide={trial.trialLocations.length === 0}
            id='findASite'
            innerRef={sectionRefs.findASite.ref}
            title='Find a Location'
          >
            <AdditionalInformation trial={trial} />
            <ClosestLocation
              trialLocations={
                trialLocationInfo?.nearestTrialLocations ?? trial.trialLocations
              }
            />
            <Trial2ListingMap trialLocations={trial.trialLocations} />
          </ListingSection>
          <LogisticInfo trial={trial} />
          {trial.nctId !== IGM_BIO_TRIAL.nctId && (
            <>
              <WhoIsRunningTheClinicalTrial trial={trial} />
              <SiteInfoPanel overrides={overrides} />
            </>
          )}
          {showMediaLibrary && (trial?.chartImages?.length ?? 0) !== 0 && (
            <ListingSection
              id='mediaLibrary'
              innerRef={sectionRefs.mediaLibrary.ref}
              title='Media Library'
            >
              <TrialMediaLibrary trial={trial} />
            </ListingSection>
          )}
          {showOtherTrialsToConsider && relatedListings?.length > 0 && (
            <RelatedListingLinks
              relatedListings={relatedListings ?? []}
              trackingSource={TrackingSources.LISTING}
            />
          )}
          {showPopularCategories && relatedDirectories?.length > 0 && (
            <RelatedConditionCarousel
              relatedDirectories={relatedDirectories ?? []}
              trackingSource={TrackingSources.LISTING}
            />
          )}
          {showRelatedArticleLinks && relatedArticles?.length > 0 && (
            <RelatedArticleLinks
              relatedArticles={relatedArticles ?? []}
              trackingSource={TrackingSources.LISTING}
            />
          )}
          <RelatedTreatmentTopicLinks
            relatedTreatmentTopics={relatedTreatmentTopics ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
        </div>
      </div>

      <FloatingApplyCta
        className={isHeroInView ? 'bottom-[-100%]' : 'bottom-0'}
        ctaColor={ctaColor}
        trial={trial}
      />
    </>
  )
}
